import { FormEvent, useRef, useState } from 'react';

import { Button, Checkbox, Flex, FormControl, FormHelperText, FormLabel, Input, Text } from '@chakra-ui/react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ButtonVariant } from '@/graphql/generated';
import { GeneralPath } from '@/utils/models';
import { libraryRequestSchema } from '@/utils/validation-schemas/library-request-schema';

type LibraryRequestData = z.infer<typeof libraryRequestSchema>;

export interface LibraryRequestFormProps {
  onSubmit: (values: LibraryRequestData & { captchaToken: string }) => void | Promise<void>;
  onError: (e?: Error) => void;
}

export const LibraryRequestForm = ({ onSubmit: onSubmitProp, onError }: LibraryRequestFormProps) => {
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const form = useForm<LibraryRequestData>({
    resolver: zodResolver(libraryRequestSchema),
    defaultValues: {
      email: '',
      name: '',
      marketingAgree: false,
    },
  });

  const { reset, register, handleSubmit, formState } = form;

  const resetCaptcha = (): void => {
    setCaptchaToken(null);
    captchaRef.current?.resetCaptcha();
  };

  const { errors, isSubmitting } = formState;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let token = captchaToken;
    if (!token) {
      const captchaResult = await captchaRef.current?.execute({ async: true });
      if (captchaResult?.response) {
        token = captchaResult?.response;
      }
    }

    handleSubmit(async (values) => {
      try {
        if (!token) {
          throw new Error('Captcha token is not available');
        }
        await onSubmitProp({
          ...values,
          captchaToken: token,
        });
        reset();
      } catch (e) {
        onError(e as unknown as Error);
      } finally {
        resetCaptcha();
      }
    })();
  };

  const onTokenVerify = async (token: string) => setCaptchaToken(token);

  return (
    <form onSubmit={onSubmit} noValidate>
      <FormControl>
        <FormLabel color="gray.700">Name</FormLabel>
        <Input
          {...register('name')}
          type="text"
          placeholder="Enter your full name"
          isReadOnly={isSubmitting}
          isInvalid={!!errors.name}
          bg="white"
        />
        <FormHelperText mb="2" color="red.500">
          <Text as="span" variant="text-xs/lineHeight-4/font-normal">
            {errors.name?.message}&nbsp;
          </Text>
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel color="gray.700">Email</FormLabel>
        <Input
          {...register('email')}
          type="email"
          placeholder="email@example.com"
          isReadOnly={isSubmitting}
          isInvalid={!!errors.email}
          bg="white"
        />
        <FormHelperText mb="2" color="red.500">
          <Text as="span" variant="text-xs/lineHeight-4/font-normal">
            {errors.email?.message}&nbsp;
          </Text>
        </FormHelperText>
      </FormControl>

      <FormControl>
        <Flex gap="3">
          <Checkbox
            {...register('marketingAgree')}
            isReadOnly={isSubmitting}
            bg="white"
            alignSelf="flex-start"
            isInvalid={!!errors.marketingAgree}
          />
          <Text variant="text-xs/lineHeight-4/font-normal/0.75rem" color="purple.500">
            I agree to receive commercial information from Startup House. Read our{' '}
            <Text textDecoration="underline" fontWeight="semibold" as={Link} href={GeneralPath.PRIVACY_POLICY}>
              Privacy Policy
            </Text>{' '}
            for more.
          </Text>
        </Flex>
        <FormHelperText mb="2" color="red.500">
          <Text as="span" variant="text-xs/lineHeight-4/font-normal">
            {errors.marketingAgree?.message}&nbsp;
          </Text>
        </FormHelperText>
      </FormControl>

      <HCaptcha
        onVerify={onTokenVerify}
        ref={captchaRef}
        size="invisible"
        sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY}
      />

      <Button w="full" type="submit" variant={ButtonVariant.Light} isLoading={isSubmitting} size="lg">
        Get your free e-book
      </Button>
    </form>
  );
};
